import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { getUserTypeList } from "src/services/user-type.services";
import { transferPackage } from "src/services/booking.services";
import { uploadImage } from "src/services/util.services";
import Select from "react-select";
import { getUserList } from "src/services/user.services";
import ReactSignatureCanvas from "react-signature-canvas";

const schema = z.object({
  type: z.string().min(1, "Please select user type"),
  employeeCode: z.string().min(1, "Please select designation"),
  bookingOrderId: z.string().min(1, "Please type the booking order id"),
  transferSignature: z.string().optional(),
  transferImage: z.string().optional(),
});

const TransferPackage = () => {
  const signatureRef = useRef();
  const [userData, setUserData] = useState(null);
  const [allUserList, setAllUserList] = useState(null);
  const [userTypeData, setUserTypeData] = useState(null);
  const [transferImage, setTransferImage] = useState(null);
  const [transferImageUrl, setTransferImageUrl] = useState("");
  const [transferSignature, setTransferSignature] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getUserTypeInfo();
  }, []);

  const getUserTypeInfo = () => {
    // setIsLoading(true)
    getUserTypeList({ limit: 20 })
      .then(async (response) => {
        const userType = response.data;
        let userTypeOptions = [
          { label: "Please select a user type", value: "" },
        ];
        userType.forEach((element) => {
          if (element.type == "cvit" || element.type == "driver") {
            const option = { label: element.name, value: element.type };
            userTypeOptions.push(option);
          }
        });
        setUserTypeData(userTypeOptions);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getUesrListByTypeQuery = (type, searchValue) => {
    let params = { type, name: searchValue ? searchValue : "" };
    getUserList(params)
      .then((resp) => {
        let uesrList = resp.data;
        if (uesrList.length > 0) {
          setAllUserList(uesrList);
          let optionsList = uesrList.map((item) => {
            return {
              value: item.employeeCode,
              label: `${item.firstName} ${item.lastName}`,
            };
          });
          setUserData(optionsList);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "",
      employeeCode: "",
      bookingOrderId: "",
      transferSignature: "",
      transferImage: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setLoading((prev) => true);
      let updatedData = data;
      // Step 1: Upload transfer package image
      if (transferImage) {
        let res = await uploadTransferPackageImage();
        console.log(res);
        if (res && res.success) {
          updatedData.transferImage = res.url;
        }
      }
      // Step 2: Upload signature
      const uploadSignatureSuccess = await uploadSignature();

      if (uploadSignatureSuccess && uploadSignatureSuccess.success) {
        updatedData.transferSignature = uploadSignatureSuccess.url;

        if (updatedData.transferSignature) {
          console.log(updatedData);
          // Step 3: Call transferPackage function
          toast
            .promise(
              transferPackage(updatedData),
              {
                loading: "Loading",
                success: "Package Transferred Successfully",
                error: (err) => {
                  return err.response &&
                    err.response.data &&
                    err.response.data.message
                    ? err.response.data.message
                    : defaultErrorMessage;
                },
              },
              {
                id: "transfer-package",
              }
            )
            .then(async (response) => {
              console.log("response", response);
              reset();
              signatureRef.current.clear();
              setTransferImage(null);
              setTransferImageUrl("");
              setTransferSignature("");
              setSelectedUserType("");
              setSelectedUser("");
              setLoading((prev) => false);
            })
            .catch((error) => {
              console.error(error);
              setLoading((prev) => false);
            });
        }
      }
    } catch (error) {
      setLoading((prev) => false);
      console.error(error);
    }
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleTransferImageFileInput = (event) => {
    const file = event.target.files[0];
    setTransferImage((prev) => file);
  };

  const uploadTransferPackageImage = async () => {
    try {
      if (transferImage && transferImageUrl == "") {
        const formData = new FormData();
        formData.append("file", transferImage);
        let res = await uploadImage(formData);
        if (res.success) {
          setValue("transferImage", res.url);
          setTransferImageUrl((prev) => res.url);
          return { success: true, url: res.url };
        } else {
          console.log("failed to upload transfer image");
          return { success: false };
        }
      } else {
        return { success: false };
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUserTypeOnChange = (selectedOption) => {
    if (
      !selectedOption ||
      (Array.isArray(selectedOption) && selectedOption.length === 0)
    ) {
      setValue("type", "");
      setSelectedUserType("");
      setSelectedUser("");
      setUserData(null);
    } else {
      if (selectedOption.value !== "") {
        setValue("type", selectedOption.value);
        setSelectedUserType(() => selectedOption);
        getUesrListByTypeQuery(selectedOption.value);
      } else {
        setValue("type", "");
        setSelectedUserType("");
        setSelectedUser("");
        setUserData(null);
      }
    }
  };
  const handleChangeUser = (selectedOption) => {
    if (selectedOption) {
      setValue("employeeCode", selectedOption.value);
      setSelectedUser((prev) => selectedOption);
    } else {
      setSelectedUser("");
    }
  };
  const handleOnInputChangeUser = (searchValue) => {
    if (searchValue) {
      console.log(searchValue);
      const matchingRoutes = allUserList.filter(
        (item) =>
          item.firstName.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.lastName.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.email.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.designation.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.employeeCode.toLowerCase().includes(searchValue.toLowerCase())
      );
      if (matchingRoutes.length <= 0) {
        getUesrListByTypeQuery(selectedUserType?.value, searchValue);
      }
    }
  };

  const uploadSignature = async () => {
    try {
      let res = signatureRef.current.isEmpty();
      if (!res && transferSignature == "") {
        let trimedSignature = signatureRef.current
          .getTrimmedCanvas()
          .toDataURL();
        // Now, you can send `trimedSignature` to your API endpoint
        // using a function for making API requests (e.g., using axios).

        // Convert the Base64 string to a Blob
        const signatureBlob = dataURItoBlob(trimedSignature);

        // Create a File object with the Blob and set additional properties
        const signatureFile = new File([signatureBlob], `${Date.now()}.png`, {
          type: "image/png",
          lastModified: Date.now(),
        });

        const formData = new FormData();
        formData.append("file", signatureFile);

        let response = await uploadImage(formData);
        if (response.success) {
          setValue("transferSignature", response.url);
          setTransferSignature((prev) => response.url);
          setLoading((prev) => false);
          return { success: true, url: response.url };
        }
      } else {
        setLoading((prev) => false);
        alert("Please draw your signature");
        return { success: false };
      }
    } catch (error) {
      setLoading((prev) => false);
      console.log(error);
    }
  };

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setTransferSignature("");
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              Transfer Package
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="userType"
                  className="col-sm-4 col-form-label"
                >
                  Transfer To:
                </CFormLabel>
                <CCol sm={8}>
                  <Select
                    id="userType"
                    value={selectedUserType}
                    onChange={handleUserTypeOnChange}
                    options={userTypeData}
                    isClearable={true}
                    placeholder="select user type"
                  />
                  {errors.type?.message && (
                    <p className="error_text">{errors.type?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="employeeCode"
                  className="col-sm-4 col-form-label"
                >
                  Select User:
                </CFormLabel>
                <CCol sm={8}>
                  <Select
                    id="employeeCode"
                    onInputChange={handleOnInputChangeUser}
                    value={selectedUser}
                    onChange={handleChangeUser}
                    options={userData}
                    isClearable={true}
                    isDisabled={
                      userData === null || userData.length < 0 ? true : false
                    }
                    placeholder="select user"
                  />
                  {errors.employeeCode?.message && (
                    <p className="error_text">{errors.employeeCode?.message}</p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel className="col-sm-4 col-form-label">
                  Employee Code:
                </CFormLabel>
                <CCol sm={8}>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedUser ? selectedUser.value : "N/A"}
                    disabled
                  />
                </CCol>
              </CRow>
            </CCol>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="bookingOrderId"
                  className="col-sm-4 col-form-label"
                >
                  Booking ID:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="bookingOrderId"
                    {...register("bookingOrderId")}
                  />
                  {errors.bookingOrderId?.message && (
                    <p className="error_text">
                      {errors.bookingOrderId?.message}
                    </p>
                  )}
                </CCol>
              </CRow>
              <CRow className="mb-3">
                <CFormLabel htmlFor="image" className="col-sm-4 col-form-label">
                  Pacakge Image:
                </CFormLabel>

                <CCol sm={8}>
                  <CFormInput
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleTransferImageFileInput}
                  />

                  {errors.transferImage?.message && (
                    <p className="error_text">
                      {errors.transferImage?.message}
                    </p>
                  )}
                </CCol>
              </CRow>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm={12}>
              <CFormLabel htmlFor="image" className="col-sm-4 col-form-label">
                Transfer Signature:
              </CFormLabel>
              <ReactSignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{
                  width: 1000,
                  height: 200,
                  className: "signature-canvas bg-secondary w-100",
                }}
                clearOnResize={false}
              />
              {errors.transferSignature?.message && (
                <p className="error_text">
                  {errors.transferSignature?.message}
                </p>
              )}
            </CCol>
          </CRow>

          <div className="d-flex justify-content-end mt-3">
            <CButton
              type="button"
              className="px-5 py-2 me-3 btn-oliveGreen"
              onClick={handleClear}
            >
              Clear Signature
            </CButton>
            <CButton
              type="submit"
              className="px-5 py-2 ms-3"
              onClick={handleSubmit}
              disabled={loading}
            >
              Transfer Package
            </CButton>
          </div>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default TransferPackage;
